// src/components/homepage/resources/ResourceData.ts

/**
 * @fileoverview Homepage "Resource Center" section data.
 * @description Credibility-building slice that visually connects our AI insights to real tools,
 * interactive research, assessments, and a deep knowledge base. Intentionally minimal copy with
 * visual proof and motion to reinforce sophistication without overwhelming the user.
 * @module homepage/resources/ResourceData
 * @exports RESOURCE_SCREENSHOTS, RESOURCE_CATEGORIES, containerVariants
 */

import type { ComponentType } from "react"
import type { Variants } from "motion/react"
import RiCalculatorLine from '~/icons/remix/generated/RiCalculatorLine'
import RiBookOpenLine from '~/icons/remix/generated/RiBookOpenLine'
import RiLightbulbLine from '~/icons/remix/generated/RiLightbulbLine'
import RiTestTubeLine from '~/icons/remix/generated/RiTestTubeLine'
import RiSmartphoneLine from '~/icons/remix/generated/RiSmartphoneLine'

/**
 * Description of a background screenshot used in the rotating gallery.
 *
 * @interface ResourceScreenshot
 * @property {string} src - Optimized static asset path under `public/` for display.
 * @property {string} alt - Human description of the visual (not announced when decorative).
 * @property {string} description - Narrative context if needed for future UI surfaces.
 * @property {string} category - High-level category label surfaced in UI.
 */
export interface ResourceScreenshot {
  src: string
  alt: string
  description: string
  category: string
}

/**
 * Resource Center Homepage Section - Homepage credibility building section
 * 
 * Subtly builds trust by showcasing the depth of knowledge and tools that power
 * AdSights' AI recommendations. Positioned between product features and performance
 * stats to answer "why should I trust these insights?" without being direct.
 * 
 * Psychological Journey:
 * 1. User just learned what the product can do (features)
 * 2. Now they wonder "how do I know this actually works?"
 * 3. This section subtly shows the expertise foundation 
 * 4. Leading into performance stats that validate the claims
 * 
 * Design Philosophy:
 * - Visual-first storytelling (less text, more impact)
 * - Matches homepage design patterns (Badge + gradient text)  
 * - Natural flow building toward "Built for Performance, Backed by Data"
 * - Implies depth rather than stating it directly
 */

// Screenshots from actual resource center tools for subtle background display
const RESOURCE_SCREENSHOTS: ReadonlyArray<ResourceScreenshot> = [
  {
    src: "/images/resource-examples/resource-center-examples-calculators-creative-testing-budget.webp",
    alt: "Creative Testing Budget Calculator with statistical modeling interface",
    description: "Advanced A/B testing budget optimization with confidence intervals",
    category: "Interactive Calculators"
  },
  {
    src: "/images/resource-examples/resource-center-examples-blog-articles-signal-noise-interactive-chart-explorer.webp", 
    alt: "Interactive signal vs noise analysis research tool",
    description: "Live data visualization with statistical pattern detection",
    category: "Research Articles"
  },
  {
    src: "/images/resource-examples/resource-center-examples-calculators-video-dropoff-rate-results-chart.webp",
    alt: "Video drop-off rate analysis dashboard with performance metrics",
    description: "Real-time video engagement analytics and optimization insights",
    category: "Interactive Calculators"
  },
  {
    src: "/images/resource-examples/resource-center-examples-quizzes-results-example.webp",
    alt: "Professional skills assessment results dashboard", 
    description: "Comprehensive competency evaluation with detailed performance feedback",
    category: "Assessment Tools"
  },
  {
    src: "/images/resource-examples/resource-center-examples-glossary-stacked-area-chart-entry.webp",
    alt: "Interactive glossary entry with data visualization examples",
    description: "In-depth marketing terminology with visual learning aids",
    category: "Knowledge Base"
  },
  {
    src: "/images/resource-examples/resource-center-examples-calculators-customer-ltv.webp",
    alt: "Customer lifetime value calculator with predictive modeling",
    description: "Advanced LTV forecasting with cohort analysis and projections",
    category: "Interactive Calculators"
  },
  {
    src: "/images/resource-examples/resource-center-examples-blog-articles-ad-performance-moving-average-chart-example.webp",
    alt: "Ad performance research with moving average trend analysis",
    description: "Statistical analysis of advertising performance patterns and trends",
    category: "Research Articles"
  },
  {
    src: "/images/resource-examples/resource-center-examples-quizzes-creative-quality-grader.webp",
    alt: "Creative quality assessment and grading interface",
    description: "AI-powered creative evaluation with actionable improvement recommendations",
    category: "Assessment Tools"
  },
  {
    src: "/images/resource-examples/resource-center-examples-blog-articles-ad-thought-experiment.webp",
    alt: "Marketing thought experiment and hypothesis testing research",
    description: "Experimental marketing methodology with data-driven insights",
    category: "Research Articles"
  },
  {
    src: "/images/resource-examples/resource-center-examples-blog-articles-ad-variability-moving-average-chart-second-example.webp",
    alt: "Advanced ad variability analysis with smoothing algorithms",
    description: "Sophisticated statistical modeling of advertising performance variance",
    category: "Research Articles"
  },
  {
    src: "/images/resource-examples/resource-center-examples-blog-articles-video-performance-analysis-timeline.webp",
    alt: "Video performance timeline analysis with engagement metrics",
    description: "Comprehensive video analytics with retention and engagement tracking",
    category: "Research Articles"
  },
  {
    src: "/images/resource-examples/resource-center-examples-calculators-mer-results-feedback.webp",
    alt: "Marketing Efficiency Ratio calculator with detailed feedback system",
    description: "Advanced MER calculation with performance benchmarking and optimization",
    category: "Interactive Calculators"
  },
  {
    src: "/images/resource-examples/resource-center-examples-calculators-video-dropoff-rate.webp",
    alt: "Video retention calculator with drop-off rate analysis",
    description: "Detailed video engagement measurement and optimization tools",
    category: "Interactive Calculators"
  },
  {
    src: "/images/resource-examples/resource-center-examples-glossary-ctr-entry.webp",
    alt: "Click-through rate glossary entry with industry benchmarks",
    description: "Comprehensive CTR definition with real-world examples and benchmarks",
    category: "Knowledge Base"
  },
  {
    src: "/images/resource-examples/resource-center-examples-quizzes-advanced-question-sorted-answer.webp",
    alt: "Advanced assessment question with sorted answer options",
    description: "Complex skills evaluation with adaptive questioning methodology",
    category: "Assessment Tools"
  },
  {
    src: "/images/resource-examples/resource-center-examples-quizzes-advanced-question-with-table.webp",
    alt: "Professional assessment with data table analysis",
    description: "Advanced competency testing with analytical reasoning components",
    category: "Assessment Tools"
  },
  {
    src: "/images/resource-examples/resource-center-examples-quizzes-roas-multiple-choice-incorrect.webp",
    alt: "ROAS assessment with detailed feedback on incorrect answers",
    description: "Educational assessment system with comprehensive learning feedback",
    category: "Assessment Tools"
  }
] as const

/**
 * Resource category card metadata for the grid section.
 *
 * @interface ResourceCategory
 * @property {ComponentType<{ className?: string }>} icon - Icon component.
 * @property {string} category - Category title displayed prominently.
 * @property {string} count - Sub-label emphasizing breadth, not exact quantity.
 * @property {string} description - Supporting explanation for the category.
 * @property {`/${string}`} href - Internal route to the category surface.
 * @property {string} visual - Tailwind classes for visual accent pill.
 * @property {ReadonlyArray<string>} examples - Examples for context (presently unused in UI).
 */
interface ResourceCategory {
  icon: ComponentType<{ className?: string }>
  category: string
  count: string
  description: string
  href: `/${string}`
  visual: string
  examples: ReadonlyArray<string>
}

// Resource categories showcasing breadth and sophistication - categories are the focus, not individual tools
const RESOURCE_CATEGORIES: ReadonlyArray<ResourceCategory> = [
  {
    icon: RiSmartphoneLine,
    category: "Mockup Generators",
    count: "11+ Design Tools",
    description: "Chat, email, review, and social post screenshots for testimonials and ad creative",
    href: "/resources/tools/generators",
    visual: "bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400",
    examples: ["Fake Chat", "Email Threads", "Reviews", "Social Posts"]
  },
  {
    icon: RiCalculatorLine,
    category: "Statistical Calculators",
    count: "8+ Advanced Tools",
    description: "Statistical modeling, A/B testing, budget optimization with confidence intervals",
    href: "/resources/tools/calculators",
    visual: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
    examples: ["Creative Testing Budget", "A/B Significance", "Customer LTV", "Video Analysis"]
  },
  {
    icon: RiLightbulbLine,
    category: "Research Articles",
    count: "Interactive Insights",
    description: "Data-driven research with live visualizations and statistical analysis",
    href: "/blog",
    visual: "bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400",
    examples: ["Signal vs Noise Analysis", "Performance Optimization", "Measurement Theory"]
  },
  {
    icon: RiTestTubeLine, 
    category: "Assessment Tools",
    count: "Professional Evaluation",
    description: "Multi-domain skills testing with detailed analytics and personalized feedback",
    href: "/resources/tools/quizzes",
    visual: "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400",
    examples: ["Media Buyer Skills", "Creative Quality", "Platform Expertise"]
  },
  {
    icon: RiBookOpenLine,
    category: "Knowledge Base",
    count: "242+ Definitions",
    description: "Comprehensive glossary with visual examples and cross-referenced concepts",
    href: "/resources/glossary",
    visual: "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400",
    examples: ["Marketing Metrics", "Statistical Terms", "Platform Concepts"]
  }
] as const

// Animation variants that match homepage style
const containerVariants: Variants = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6, // Reduced duration for snappier feel
      staggerChildren: 0.15, // Slightly faster stagger
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
}

export { RESOURCE_SCREENSHOTS, RESOURCE_CATEGORIES, containerVariants }
